import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'app_data_usage_v1';

// Helper to get stored usage data
export const getStoredDataUsage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { totalBytes: 0, routes: {} };
  } catch (e) {
    console.error('Failed to read data usage from localStorage', e);
    return { totalBytes: 0, routes: {} };
  }
};

// Helper to reset data
export const clearDataUsage = () => {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event('data-usage-updated'));
};

export const useDataUsageTracker = () => {
  const location = useLocation();
  const currentRouteRef = useRef(location.pathname);

  // Keep route ref up-to-date for async network callbacks
  useEffect(() => {
    currentRouteRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      return;
    }

    const handleEntries = (entries) => {
      let addedBytes = 0;
      const currentRoute = currentRouteRef.current || 'unknown';

      const usage = getStoredDataUsage();

      entries.getEntries().forEach((entry) => {
        // transferSize: bytes transferred over network (0 if loaded from disk/memory cache)
        // Fallback to encodedBodySize if transferSize is unavailable due to CORS policies
        const bytes = entry.transferSize > 0 ? entry.transferSize : (entry.encodedBodySize || 0);

        if (bytes > 0) {
          addedBytes += bytes;
          usage.routes[currentRoute] = (usage.routes[currentRoute] || 0) + bytes;
        }
      });

      if (addedBytes > 0) {
        usage.totalBytes += addedBytes;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
        // Dispatch custom event so the UI updates instantly across tabs/components
        window.dispatchEvent(new Event('data-usage-updated'));
      }
    };

    let observer;
    try {
      observer = new PerformanceObserver(handleEntries);
      // buffered: true captures resources loaded prior to component mounting
      observer.observe({ type: 'resource', buffered: true });
    } catch (e) {
      console.warn('PerformanceObserver resource tracking not supported', e);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);
};